import { useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import api from "../api/api";
import toast from "react-hot-toast";

const MandatoryRedirector = () => {
  const userId = localStorage.getItem("userId");
  const username = localStorage.getItem("username");
  const token = localStorage.getItem("token");

  const history = useHistory();
  const location = useLocation();

  // ✅ Track current mandatory (id + type)
  const currentMandatoryRef = useRef({ id: null, type: null });

  const isMounted = useRef(true);
  const isSseConnected = useRef(false);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // 🔒 REDIRECT HANDLER (SMART + NO DUPLICATE TOAST)
  const redirectToMandatory = (id, type, source = "") => {
    if (!isMounted.current) return;

    const pathMap = {
      SURVEY: `/take/${id}`,
      OFFER: `/offers`,
      TRAINING: `/training-learning`,
    };

    const targetPath = pathMap[type];
    if (!targetPath) return;

    const isSameMandatory =
      currentMandatoryRef.current.id === id &&
      currentMandatoryRef.current.type === type;

    console.log(`🔒 Redirect [${source}] →`, type, id);

    // ✅ Show toast only if new OR user trying wrong page
    if (!isSameMandatory || location.pathname !== targetPath) {
      toast.dismiss();
      toast.success(`Complete mandatory ${type.toLowerCase()} first!`, {
        duration: 1000,
      });
    }

    currentMandatoryRef.current = { id, type };

    // 🔥 ALWAYS enforce redirect
    if (location.pathname !== targetPath) {
      history.replace(targetPath);
    }
  };

  // 🔥 CENTRAL CHECK (PRIORITY BASED)
  const checkMandatory = async (source = "") => {
    try {
      const [surveyRes, offerRes, trainingRes] = await Promise.all([
        api.fetchMandatorySurvey(userId),
        api.fetchOffersForUser(userId),
        api.fetchUserTrainings(userId),
      ]);

      const survey = surveyRes.data?.data?.content?.find(
        (i) => i.isMandatory && !i.isParticipated,
      );

      const offer = offerRes.data?.data?.find(
        (i) => i.isMandatory && !i.isViewed,
      );

      const training = trainingRes.data?.data?.find(
        (i) =>
          i.isMandatory && !(i.status === "completed" || (i.progress ?? 0) > 0),
      );

      // 🔥 PRIORITY: SURVEY > OFFER > TRAINING
      if (survey) {
        return redirectToMandatory(survey.id, "SURVEY", source);
      }

      if (offer) {
        return redirectToMandatory(offer.id, "OFFER", source);
      }

      if (training) {
        return redirectToMandatory(training.id, "TRAINING", source);
      }

      // ✅ No mandatory → reset
      currentMandatoryRef.current = { id: null, type: null };
    } catch (err) {
      console.error(err);
    }
  };

  // 🔁 RUN ON ROUTE CHANGE
  useEffect(() => {
    if (!userId) return;

    const timeout = setTimeout(() => {
      checkMandatory("REST");
    }, 200);

    return () => clearTimeout(timeout);
  }, [location.pathname, userId]);

  // 📡 SSE LISTENER
  useEffect(() => {
    if (!userId || !username || !token) return;

    const handleMandatoryPush = (data) => {
      if (!data?.isMandatory || !data?.id || !data?.type) return;

      redirectToMandatory(data.id, data.type, "SSE");
    };

    api.connectSse({
      username,
      token,
      onOpen: () => {
        console.log("🟢 SSE connected");
        isSseConnected.current = true;
      },
      onError: () => {
        console.log("🔴 SSE error");
        isSseConnected.current = false;
      },
      onAuthError: () => {
        console.warn("🔒 SSE auth error");
        isSseConnected.current = false;
      },
      onMandatoryPush: handleMandatoryPush,
    });

    return () => {
      api.disconnectSse();
      isSseConnected.current = false;
    };
  }, [userId, username, token]);

  // ⏱️ POLLING FALLBACK
  useEffect(() => {
    if (!userId) return;

    const interval = setInterval(() => {
      if (isSseConnected.current) return;

      console.log("⏱️ Polling fallback...");
      checkMandatory("POLLING");
    }, 20000);

    return () => clearInterval(interval);
  }, [userId]);

  return null;
};

export default MandatoryRedirector;
